import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useAutoFillCredentials } from '@allie/mfe';
import { AllieModal, LoginEmailPin, LoginEmailPinProps } from '@allie/app-ui-lib';
import { useAllieAuth, useAppTranslations } from '../../hooks';
import { config } from '../../helpers';


export const LoginModal: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [onDismiss, setOnDismiss] = useState<() => void>();
  const [onSuccess, setOnSuccess] = useState<() => any>();
  const [onError, setOnError] = useState<() => void>();
  const [shouldRestoreLastPage, setShouldRestoreLastPage] = useState<boolean>(false);
  const [showErrorMessage, setShowErrorMessage] = useState<boolean>(false);
  const [showErrorPinMessage, setShowErrorPinMessage] = useState<boolean>(false);
  const [showMsgError, setShowMsgError] = useState<boolean>(false);
  const modalRef = useRef<HTMLIonModalElement>(null);
  const { openLoginFn, pinLogin, emailLogin, isAuthenticated, isInternalAccess } = useAllieAuth();
  const {
    MODAL_LOGIN_PIN_LABEL_CANCEL,
    MODAL_LOGIN_EMAIL_LABEL_EMAIL,
    MODAL_LOGIN_PIN_LABEL_TITLE,
    MODAL_LOGIN_EMAIL_LABEL_PASSWORD,
    MODAL_LOGIN_EMAIL_MESSAGE_INVALID_EMAIL,
    MODAL_LOGIN_EMAIL_LABEL_BUTTON,
    MODAL_LOGIN_EMAIL_LABEL,
    MODAL_LOGIN_PIN_LABEL,
    MODAL_LOGIN_EMAIL_ERROR_MESSAGE,
    MODAL_LOGIN_PIN_ERROR_MESSAGE,
    MODAL_LOGIN_ERROR_UNAUTHORIZED,
    MODAL_LOGIN_ERROR_GUEST,
    MODAL_SHOW_PIN,
    MODAL_HIDE_PIN,
    MODAL_LOGIN_FORGOT_PASSWORD_LABEL,
    MODAL_LOGIN_REMEMBER_USER,
  } = useAppTranslations();
  const { getCredentials, saveCredentials } = useAutoFillCredentials(config.AUTO_FILL_SECRET);
  const [autoFillCredentialsEnabled, setAutoFillCredentialsEnabled] = useState(true);
  const [pinEnabled, setPinEnabled] = useState(false);

  const onForgotPassword = (email?: string) => {
    window.location.href = `/login/forgot-password?origin=${
      window.location.pathname}${email ? `&email=${email}` : ''}
    `;
  };

  useEffect(() => {
    const fn = async () => setPinEnabled(await isInternalAccess());
    fn();
  }, []);

  useEffect(() => {
    if (openLoginFn) {
      const {
        onDismiss: onDismissCb,
        onSuccess: onSuccessCb,
        onError: onErrorCb,
        shouldRestoreLastPage: shouldRestoreLastPageParam = false,
        showMessageError,
      } = openLoginFn;

      setIsOpen(true);
      setOnDismiss(() => onDismissCb);
      setOnSuccess(() => onSuccessCb);
      setOnError(() => onErrorCb);
      setShouldRestoreLastPage(shouldRestoreLastPageParam);
      setShowErrorMessage(false);
      setShowErrorPinMessage(false);
      setShowMsgError(showMessageError || false);
    }
  }, [openLoginFn]);

  const onSuccessWrapper = useCallback((credentials?: string[]) => {
    credentials && saveCredentials(credentials[0], credentials[1], autoFillCredentialsEnabled);
    modalRef.current?.dismiss(true);
    if (onSuccess) return onSuccess();
  }, [onSuccess, autoFillCredentialsEnabled]);

  const onErrorWrapper = useCallback(() => {
    setShowErrorMessage(true);
    if (onError) return onError();
  }, [onError]);

  const onErrorPinWrapper = useCallback(() => {
    setShowErrorPinMessage(true);
    if (onError) return onError();
  }, [onError]);

  const loginEmailPinProps: LoginEmailPinProps = {
    pinProps: {
      labelConfirm: MODAL_LOGIN_EMAIL_LABEL_BUTTON,
      labelCancel: MODAL_LOGIN_PIN_LABEL_CANCEL,
      labelTitle: MODAL_LOGIN_PIN_LABEL_TITLE,
      errorMessage: MODAL_LOGIN_PIN_ERROR_MESSAGE,
      showError: showErrorPinMessage,
      showLabel: MODAL_SHOW_PIN,
      hideLabel: MODAL_HIDE_PIN,
      onCancel: () => {
        modalRef.current?.dismiss(false);
      },
      onSubmit: (pin: string) =>
        pinLogin(pin, onSuccessWrapper, onErrorPinWrapper, shouldRestoreLastPage),
    },
    emailProps: {
      labelEmail: MODAL_LOGIN_EMAIL_LABEL_EMAIL,
      labelPassword: MODAL_LOGIN_EMAIL_LABEL_PASSWORD,
      labelButton: MODAL_LOGIN_EMAIL_LABEL_BUTTON,
      messageInvalidEmail: MODAL_LOGIN_EMAIL_MESSAGE_INVALID_EMAIL,
      errorMessage: MODAL_LOGIN_EMAIL_ERROR_MESSAGE,
      showError: showErrorMessage,
      labelCancel: MODAL_LOGIN_PIN_LABEL_CANCEL,
      labelForgotPassword: MODAL_LOGIN_FORGOT_PASSWORD_LABEL,
      labelRememberUser: MODAL_LOGIN_REMEMBER_USER,
      autoFillCredentials: autoFillCredentialsEnabled,
      getCredentials,
      onAutoFillCredentials: setAutoFillCredentialsEnabled,
      onForgotPassword,
      onCancel: () => {
        modalRef.current?.dismiss(false);
      },
      onSubmit: (email: string, password: string) =>
        emailLogin(
          email,
          password,
          () => onSuccessWrapper([email, password]),
          onErrorWrapper,
          shouldRestoreLastPage,
        ),
    },
    pinLabel: MODAL_LOGIN_PIN_LABEL,
    emailLabel: MODAL_LOGIN_EMAIL_LABEL,
    infoWarning: {
      show: showMsgError,
      message: isAuthenticated ? MODAL_LOGIN_ERROR_UNAUTHORIZED : MODAL_LOGIN_ERROR_GUEST,
    },
  };

  return (
    <AllieModal
      ref={modalRef}
      style={{ textAlign: 'center', transition: 'height 0.4s linear' }}
      isOpen={isOpen}
      width="500px"
      onDidDismiss={
        ((e: CustomEvent) => {
          const isSuccess = e.detail.data;
          if (onDismiss && !isSuccess) onDismiss();
          setIsOpen(false);
        }) as any
      }
    >
      <div style={{ padding: '20px 32px', ...(!pinEnabled && { paddingTop: 40 }) }}>
        <LoginEmailPin {...loginEmailPinProps} pinEnabled={pinEnabled} />
      </div>
    </AllieModal>
  );
};
